import {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
  WorkspaceReservationStatus,
  showTimedFeedback,
  useAppDispatch,
  useAppSelector,
  withAsyncThunkErrorHandling,
} from '@lib/store';
import {actions} from '@lib/store/src/data/timeslots/actions';
import {WorkspaceReservation} from '@lib/store/src/data/timeslots/apiTypes';
import {getWorkdaysForDate} from '@lib/store/src/data/timeslots/selectors';
import {useReservationDetails} from '@lib/store/src/ui/hooks/timeslots/useReservationDetails';
import {useConfirmationDialog} from './useConfirmationDialog';
import {dateKeyForDateTimeString} from '@lib/store/src/data/timeslots/dateTimeHelpers';

type ReturnType = {
  isSaving: boolean;
  onAcceptInvitation: (onCompleted?: () => void) => void;
  onDeclineInvitation: (onCompleted?: () => void) => void;
};

export const useAcceptDeclineWorkspaceInvitation_timeslots = (
  workspaceReservation: WorkspaceReservation,
): ReturnType => {
  const dateKey = dateKeyForDateTimeString(workspaceReservation.localStart);

  // TODO: Support multiple
  const workday = useAppSelector(getWorkdaysForDate(dateKey))?.data?.at(0);

  const dispatch = useAppDispatch();
  const {t} = useTranslation();

  const {confirmationDialog} = useConfirmationDialog();
  const [isSaving, setIsSaving] = useState(false);

  const details = useReservationDetails(workspaceReservation);
  const currentMember = details.status === 'fulfilled' ? details.data.find((d) => d.isCurrentUser) : undefined;

  const handleUpdateGroupWorkspaceReservationStatus = async (
    reservationStatus: Extract<WorkspaceReservationStatus, 'InvitationDeclined' | 'Confirmed'>,
  ) => {
    setIsSaving(true);

    const isConfirming = reservationStatus === 'Confirmed';

    // TODO: Cancel overlapping reservations
    if (isConfirming) {
      if (workday)
        await dispatch(withAsyncThunkErrorHandling(() => actions.cancelWorkdayIncludingReservations({workday})));
    }

    const action = isConfirming
      ? actions.acceptReservationInvitationWithAutoWorkday({workspaceReservation})
      : actions.declineReservationInvitation({workspaceReservation});

    const {success} = await dispatch(withAsyncThunkErrorHandling(() => action));

    if (success && isConfirming) {
      const getMessage = () => {
        if (currentMember?.isOrganizer) {
          return t('workplace:ReplacedGroupBookingMessage');
        }

        if (workspaceReservation) {
          return t('workplace:ReplacedBookingMessage');
        }

        return t('workplace:DeskHasBeenBooked');
      };

      const message = getMessage();

      dispatch(
        showTimedFeedback({
          type: 'success',
          message,
        }),
      );
    }

    setIsSaving(false);
  };

  const handleOnDeclineInvitation = async (onCompleted?: () => void) => {
    await handleUpdateGroupWorkspaceReservationStatus('InvitationDeclined');
    onCompleted?.();
  };

  const handleOnAcceptInvitation = async (onCompleted?: () => void) => {
    if (workspaceReservation) {
      const dialogText = currentMember?.isOrganizer
        ? t('workplace:ReplaceGroupBookingDialogText')
        : t('workplace:ReplaceBookingDialogText');

      const dialogTitle = currentMember?.isOrganizer
        ? t('workplace:ReplaceGroupBookingDialogTitle')
        : t('workplace:ReplaceCurrentBookingDialogTitle');

      const result = await confirmationDialog(
        dialogTitle,
        dialogText,
        'warning',
        t('workplace:ReplaceBookingDialogPositiveButton'),
      );

      if (!result) return;
    }

    await handleUpdateGroupWorkspaceReservationStatus('Confirmed');
    onCompleted?.();
  };

  return {
    isSaving,
    onAcceptInvitation: handleOnAcceptInvitation,
    onDeclineInvitation: handleOnDeclineInvitation,
  };
};
